/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Carousel, CarouselNavigation, CarouselSlide, CarouselViewport } from '@coursera-web/cds-labs';
import { XdpV1_Org_Coursera_Degree_Stored_Api_UpsellType as UpsellTypes } from '__generated__/graphql-types';

import { Typography2, breakpoints } from '@coursera/cds-core';

import UpsellCard from 'bundles/degree-description/components/modules/upsells/UpsellCard';
import Container from 'bundles/degree-description/components/shared/Container';
import { useDegreeDescriptionUpsellsData } from 'bundles/degree-description/contexts/DegreeDescriptionPageUpsellsContext';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import {
  filterStackableItemsByUpsellType,
  getProductVariantString,
  getUpsellInfo,
} from 'bundles/degree-description/utils/utils';

import _t from 'i18n!nls/degree-description';

import { getStackabilityDummyContent } from './dummyContent';

const styles = {
  module: css`
    padding: var(--cds-spacing-400) 0;
  `,
  panel: css`
    display: grid;
    grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
    gap: var(--cds-spacing-600);
    padding: var(--cds-spacing-600);
    border-radius: 24px;
    background-color: var(--cds-color-neutral-background-primary-weak);

    ${breakpoints.down('md')} {
      grid-template-columns: minmax(0, 1fr);
      gap: var(--cds-spacing-400);
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-300);
    }
  `,
  heading: css`
    margin: 0 0 var(--cds-spacing-200);
  `,
  description: css`
    margin: 0;
  `,
  valueProps: css`
    display: grid;
    gap: var(--cds-spacing-300);
    margin: var(--cds-spacing-400) 0 0;
    padding: 0;
    list-style: none;
  `,
  valueProp: css`
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr);
    gap: var(--cds-spacing-150);
    align-items: start;
  `,
  icon: css`
    display: flex;
    align-items: flex-start;
  `,
  valuePropHeading: css`
    margin: 0 0 var(--cds-spacing-50);
  `,
  valuePropDescription: css`
    margin: 0;
  `,
  carousel: css`
    min-width: 0;
  `,
  navigation: css`
    display: flex;
    justify-content: flex-end;
    margin-bottom: calc(var(--cds-spacing-200) + 12px);
  `,
  slide: css`
    flex: 0 0 100%;
    min-width: 0;

    ${breakpoints.only('sm')} {
      flex-basis: calc((100% - 12px) / 2);
    }

    ${breakpoints.up('md')} {
      flex-basis: calc((100% - 24px) / 3);
    }

    .cds-ProductCard-grid {
      min-width: 0;
    }
  `,
  slidesContainer: css`
    gap: 12px;
  `,
  viewportBleed: css`
    margin: -12px;
  `,
  viewport: css`
    padding: 12px;
    box-sizing: border-box;
  `,
};

const StackabilityModule = (): React.ReactElement | null => {
  const { stackableItemsWithUpsellType } = useDegreeDescriptionUpsellsData();
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const pageSectionRef = useGetViewPageSectionRef('upsell');
  const products = React.useMemo(() => {
    const items = stackableItemsWithUpsellType ?? [];
    return [UpsellTypes.OffersCredit, UpsellTypes.PartOf]
      .flatMap((upsellType) => filterStackableItemsByUpsellType(items, upsellType) ?? [])
      .flatMap((item) => {
        const upsellInfo = getUpsellInfo([item]);
        return upsellInfo?.upsell ? [{ item, upsellInfo, product: upsellInfo.upsell }] : [];
      });
  }, [stackableItemsWithUpsellType]);

  if (products.length === 0) {
    return null;
  }

  const { heading, description, valueProps } = getStackabilityDummyContent();

  return (
    <div ref={pageSectionRef} css={styles.module}>
      <Container>
        <div css={styles.panel}>
          <div>
            <Typography2 variant="titleMedium" component="h2" css={styles.heading}>
              {heading}
            </Typography2>
            {description && (
              <Typography2 variant="bodyPrimary" color="supportText" component="p" css={styles.description}>
                {description}
              </Typography2>
            )}
            <ul css={styles.valueProps}>
              {valueProps.map(({ icon, headline, description: valuePropDescription }) => (
                <li key={headline} css={styles.valueProp}>
                  <span css={styles.icon}>{icon}</span>
                  <div>
                    <Typography2 variant="subtitleMedium" component="h3" css={styles.valuePropHeading}>
                      {headline}
                    </Typography2>
                    <Typography2
                      variant="bodySecondary"
                      color="supportText"
                      component="p"
                      css={styles.valuePropDescription}
                    >
                      {valuePropDescription}
                    </Typography2>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div css={styles.carousel}>
            <Carousel options={{ slidesToScroll: 'auto' }} aria-label={_t('Degree coursework')}>
              <div css={styles.navigation}>
                <CarouselNavigation navigationVariant="default" />
              </div>
              <div css={styles.viewportBleed}>
                <CarouselViewport css={styles.viewport} containerCss={styles.slidesContainer}>
                  {products.map(({ item, upsellInfo, product }, index) => (
                    <CarouselSlide
                      key={`${item.upsellType}-${product.id}`}
                      slideCss={styles.slide}
                      aria-label={product.name || undefined}
                    >
                      <UpsellCard
                        productId={product.id}
                        title={product.name || ''}
                        bannerImageUrl={upsellInfo.upsellHeaderImage}
                        slug={product.slug}
                        productVariant={product.productVariant || ''}
                        partners={upsellInfo.upsellPartners}
                        productUrl={upsellInfo.url}
                        tagline={upsellInfo.tagline}
                        footerText={[getProductVariantString(product.productVariant)].filter(Boolean)}
                        loading={false}
                        trackingNamePrefix="redesign_stackability"
                        trackingSectionName="upsell"
                        index={index}
                        isFixedHeight={false}
                      />
                    </CarouselSlide>
                  ))}
                </CarouselViewport>
              </div>
            </Carousel>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default StackabilityModule;
